// use-update-team-member.ts — Teams suite, action
//
// PATCH /api/teams/:teamId/members/:memberId. Updates a single member.

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { UpdateMemberRequest, UpdateTeamMemberResponse } from '@cabal/shared/domain/session/team/member/types.js';
import { useApi } from '@/contexts/api';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import { teamsDetailKey, teamsRootKey } from './team-query-keys';

export interface UpdateTeamMemberVars {
  teamId: string;
  memberId: string;
  data: UpdateMemberRequest;
}

export function useUpdateTeamMember(): ActionResult<UpdateTeamMemberVars, UpdateTeamMemberResponse> {
  const api = useApi();
  const queryClient = useQueryClient();

  return useActionHook<UpdateTeamMemberVars, UpdateTeamMemberResponse>(
    useMemo(
      () => ({
        mutationFn: ({ teamId, memberId, data }) =>
          api.teams.byId(teamId).members.byId(memberId).update({ body: data }),
        onInvalidate: (_data, { teamId }) => {
          queryClient.invalidateQueries({ queryKey: teamsDetailKey(teamId) });
          queryClient.invalidateQueries({ queryKey: teamsRootKey });
        },
      }),
      [api, queryClient],
    ),
  );
}
